import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { CREATE_RAW_MATERIAL } from '../../graphql/costing-mutations'
import { GET_RAW_MATERIALS } from '../../graphql/costing-queries'
import { eurosToMillicents, unitLabel } from '../../lib/costingUtils'
import toast from 'react-hot-toast'

const UNITS = ['g', 'kg', 'ml', 'l', 'piece'] as const

const CreateRawMaterialPage = () => {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [unit, setUnit] = useState<typeof UNITS[number]>('g')
  const [notes, setNotes] = useState('')
  const [initialCostEuros, setInitialCostEuros] = useState('')

  const [createRawMaterial, { loading }] = useMutation(CREATE_RAW_MATERIAL, {
    refetchQueries: [{ query: GET_RAW_MATERIALS, variables: { filters: {} } }],
    onCompleted: (data) => {
      toast.success(`"${data.createRawMaterial.name}" created`)
      navigate(`/costing/raw-materials/${data.createRawMaterial.id}`)
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    const costEuros = parseFloat(initialCostEuros)
    if (isNaN(costEuros) || costEuros < 0) {
      toast.error('Enter a valid initial cost (0 or more)')
      return
    }
    createRawMaterial({
      variables: {
        data: {
          name: name.trim(),
          unit,
          notes: notes.trim() || undefined,
          initial_cost_per_unit_millicents: eurosToMillicents(costEuros),
        },
      },
    })
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/costing/raw-materials"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">New Raw Material</h1>
          <p className="mt-1 text-sm text-gray-500">Add an ingredient and set its initial cost.</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Chicken Breast"
            className="input w-full"
            required
          />
        </div>

        {/* Unit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit <span className="text-red-500">*</span>
          </label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as typeof UNITS[number])}
            className="input w-full"
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            The unit used for all quantities and costs. Cannot be changed after creation.
          </p>
        </div>

        {/* Initial Cost */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Initial cost per {unit} (€) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={initialCostEuros}
              onChange={(e) => setInitialCostEuros(e.target.value)}
              placeholder="0.0000"
              className="input pl-7 w-full"
              required
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Enter 0 if the price is not yet known. You can update it later.
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Fresh, boneless"
            rows={3}
            className="input w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Creating…' : 'Create Raw Material'}
          </button>
          <Link to="/costing/raw-materials" className="btn-secondary">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  )
}

export default CreateRawMaterialPage
